import * as React from "react";

import Stack from "@mui/material/Stack";

import type {
    OffsetResult,
    AppWorkoutListItem,
    ContentIndexWithRelations,
    UserAtAGlance,
    GoalProgressJson,
    UserProfileDto,
} from "@volley/data";

import fetchApi, { logFetchError } from "../../util/fetchApi";
import ShareSnackbar from "../Share/ShareSnackbar";
import { useSelectedSport } from "../common/context/sport";
import { useStatus } from "../hooks/status";

import HomeCarousel from "./HomeCarousel";
import { HomeClubInClubProvider } from "./HomeClubInClubContext";
import { HomeDuprProvider } from "./HomeDuprContext";
import { HomePtiProvider } from "./HomePtiContext";
import HomeRecommendation from "./HomeRecommendation";
import HomeTopSlot from "./HomeTopSlot";
import NewHomeScreenLibrary from "./NewHomeScreenLibrary";
import useHomeRecommendation from "./useHomeRecommendation";

interface CommunityClub {
    name: string;
    label: string;
    providerId: number;
}

export default function NewHomeScreen(): React.JSX.Element {
    const { selected: sport } = useSelectedSport();
    const statusValue = useStatus();
    const [atAGlance, setAtAGlance] = React.useState<UserAtAGlance | null>(
        null,
    );
    const [goalData, setGoalData] = React.useState<GoalProgressJson | null>(
        null,
    );
    const [homeClub, setHomeClub] = React.useState<CommunityClub | null>(null);
    const [trainerClub, setTrainerClub] = React.useState<CommunityClub | null>(
        null,
    );
    const [yearInReviewYears, setYearInReviewYears] = React.useState<number[]>(
        [],
    );
    const [homeClubWorkouts, setHomeClubWorkouts] = React.useState(false);
    const [homeClubClasses, setHomeClubClasses] = React.useState(false);
    // Consumed from the URL on mount: Club-in-Club's onboarding ends by sending the
    // member here with `?recommend=assessment`.
    const recommendation = useHomeRecommendation();

    React.useEffect(() => {
        async function fetchData() {
            try {
                const trainerId = statusValue.status?.clientId;

                const [
                    atAGlanceData,
                    goalProgressData,
                    userProfileData,
                    trainerClubData,
                    yearInReviewData,
                ] = await Promise.all([
                    // Every member catches for itself. Two of these used to
                    // reject into the batch, so one slow or unauthorized call
                    // — at-a-glance runs to 10s and fetchApi aborts there —
                    // discarded the whole screen's data, leaving `atAGlance`
                    // null for the life of the page (issue #8335).
                    fetchApi<UserAtAGlance>("/api/stats/at-a-glance").catch(
                        (e: unknown) => {
                            logFetchError(
                                e as Error,
                                "Could not fetch stats at-a-glance",
                            );
                            return null;
                        },
                    ),
                    fetchApi<GoalProgressJson | null>(
                        "/api/stats/goals/active/progress",
                    ).catch((e: unknown) => {
                        logFetchError(
                            e as Error,
                            "Could not fetch active goal progress",
                        );
                        return null;
                    }),
                    fetchApi<UserProfileDto>("/api/users/me").catch(
                        (e: unknown) => {
                            logFetchError(
                                e as Error,
                                "Could not fetch user profile",
                            );
                            return null;
                        },
                    ),
                    trainerId
                        ? fetchApi<OffsetResult<AppWorkoutListItem>>(
                              `/api/app-workouts/trainer-club/${trainerId}?sport=${sport}&limit=1&offset=0`,
                          ).catch(() => ({ count: 0, result: [] }))
                        : Promise.resolve({ count: 0, result: [] }),
                    fetchApi<{ years: number[] }>(
                        "/api/year-in-review/available",
                    ).catch(() => ({ years: [] })),
                ]);

                if (atAGlanceData) setAtAGlance(atAGlanceData);
                setGoalData(goalProgressData);
                setYearInReviewYears(yearInReviewData.years);

                // Get home club info from user profile's home location
                const homeLocation = userProfileData?.profile?.homeLocation;
                const homeClubProviderId =
                    homeLocation?.contentProvider?.id ?? null;

                if (homeLocation && homeLocation.contentProvider) {
                    const homeClubWorkouts = await fetchApi<
                        OffsetResult<AppWorkoutListItem>
                    >(
                        `/api/app-workouts/home-club?sport=${sport}&limit=1&offset=0`,
                    );
                    setHomeClub({
                        name: homeLocation.contentProvider.name,
                        label: homeLocation.name,
                        providerId: homeLocation.contentProvider.id,
                    });
                    setHomeClubWorkouts(homeClubWorkouts.count > 0);
                } else {
                    setHomeClub(null);
                }

                // If we have a home club provider, check for classes
                if (homeClubProviderId) {
                    const classesData = await fetchApi<
                        OffsetResult<ContentIndexWithRelations>
                    >(
                        `/api/content/search?type=JOURNEY&journeyType=class&sport=${sport}&providerId=${homeClubProviderId}&limit=1&offset=0`,
                    ).catch(() => ({ count: 0, result: [] }));

                    setHomeClubClasses(classesData.count > 0);
                } else {
                    setHomeClubClasses(false);
                }

                if (
                    trainerClubData.count > 0 &&
                    trainerClubData.result[0]?.provider
                ) {
                    const trainerProviderId =
                        trainerClubData.result[0].provider.id;
                    // Only show trainer club if different from home club
                    if (trainerProviderId !== homeClubProviderId) {
                        setTrainerClub({
                            name: trainerClubData.result[0].provider.name,
                            label: trainerClubData.result[0].provider.label,
                            providerId: trainerProviderId,
                        });
                    } else {
                        setTrainerClub(null);
                    }
                } else {
                    setTrainerClub(null);
                }
            } catch (e) {
                logFetchError(e as Error, "Could not fetch home screen data");
            }
        }

        fetchData();
    }, [sport, statusValue.status?.clientId]);

    const homeCarousel = (
        <HomeCarousel
            atAGlance={atAGlance}
            goalData={goalData}
            yearInReviewYears={yearInReviewYears}
        />
    );

    return (
        <Stack
            sx={{
                position: "relative",
                overscrollBehavior: "contain",
                px: 1,
                pb: 12,
                backgroundColor: (t) => t.palette.primary.dark,
            }}
        >
            {/* Wraps the screen rather than just the widget: the top slot needs to
                know whether this user is in the Club-in-Club cohort before it can
                decide what belongs there. Owns its own request — deliberately not
                part of the batch above, for the same reason the DUPR and PTI
                providers were extracted (see #8335). */}
            <HomeClubInClubProvider>
                {/* Scoped to home rather than the app: the carousel's status strip
                    and the library's assessment entry both need the same DUPR
                    standing, and each used to fetch it separately. */}
                <HomeDuprProvider>
                    <HomePtiProvider>
                        {/* Ask Volley, or the Club-in-Club widget for the trial
                            cohort. The carousel always renders beneath it. */}
                        <HomeTopSlot />
                        {/* Only when the URL asked for it; null otherwise. */}
                        <HomeRecommendation recommendation={recommendation} />
                        {homeCarousel}

                        <NewHomeScreenLibrary
                            homeClub={homeClub}
                            homeClubClasses={homeClubClasses}
                            homeClubWorkouts={homeClubWorkouts}
                            trainerClub={trainerClub}
                        />
                    </HomePtiProvider>
                </HomeDuprProvider>
            </HomeClubInClubProvider>

            <ShareSnackbar />
        </Stack>
    );
}
